import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  TenantsDeliveryStatus as DeliveryStatus,
  TenantsWebhook as Webhook,
  TenantsWebhookCreate as WebhookCreate,
  TenantsWebhookDelivery as WebhookDelivery,
  TenantsWebhookEventType as WebhookEventType,
  TenantsWebhookProbeResult as WebhookProbeResult,
  TenantsWebhookUpdate as WebhookUpdate,
} from '../generated/api/types.gen.ts';
import { webhooksCreate, webhooksList, webhooksListDeliveries, webhooksRemove, webhooksRetryDelivery, webhooksRotateSecret, webhooksTest, webhooksUpdate, webhooksVerify } from '../generated/api/sdk.gen.ts';
import { ApiFeil } from './feil.ts';
import { useApiKlient } from './klient.ts';

export type { DeliveryStatus, Webhook, WebhookCreate, WebhookDelivery, WebhookEventType, WebhookProbeResult, WebhookUpdate };

/** Gruppene hendelsene står i (H3.5, konseptsiden skjerm 14): én avkrysning og ett antall per gruppe. */
export type Hendelsesgruppe = 'innspill' | 'betaprogram' | 'hypoteser';

export const GRUPPER: ReadonlyArray<{ id: Hendelsesgruppe; navn: string }> = [
  { id: 'innspill', navn: 'Innspill' },
  { id: 'betaprogram', navn: 'Betaprogram' },
  { id: 'hypoteser', navn: 'Hypoteser' },
];

/**
 * Alle hendelsene i kontrakten med et menneskelig ord ved siden av, i tre grupper. Ledningsnavnet
 * må stå — det er dette utvikleren deres matcher på — men bare bak «Vis tekniske navn» (DD-50,
 * DD-60; UX-gjennomgangen 2026-09-14, B8). Hypotesehendelsene står i den rekkefølgen en hypotese
 * går gjennom dem, og `submission.conclusion_notice` står blant dem: det er hypotesens eier som
 * utløser den, og den finnes bare med rommet (konseptsidens avvik 17).
 */
export const HENDELSER: ReadonlyArray<{ navn: WebhookEventType; gjelder: string; gruppe: Hendelsesgruppe }> = [
  { navn: 'submission.created', gjelder: 'en sak kom inn', gruppe: 'innspill' },
  { navn: 'submission.status_changed', gjelder: 'en sak fikk ny status', gruppe: 'innspill' },
  { navn: 'submission.replied', gjelder: 'en sak fikk svar', gruppe: 'innspill' },
  { navn: 'submission.deadline_warning', gjelder: 'en frist nærmer seg', gruppe: 'innspill' },
  { navn: 'known_issue.fixed', gjelder: 'en kjent feil ble rettet', gruppe: 'innspill' },
  { navn: 'subject.deleted', gjelder: 'et pseudonym ble slettet', gruppe: 'innspill' },
  { navn: 'handoff.completed', gjelder: 'en overlevering er fullført', gruppe: 'innspill' },
  { navn: 'program.invitation_created', gjelder: 'en invitasjon er klar til å sendes', gruppe: 'betaprogram' },
  { navn: 'program.participant_joined', gjelder: 'en tester takket ja', gruppe: 'betaprogram' },
  { navn: 'program.participant_withdrawn', gjelder: 'en tester gikk ut', gruppe: 'betaprogram' },
  { navn: 'program.segment_changed', gjelder: 'segmentet endret seg', gruppe: 'betaprogram' },
  { navn: 'program.follow_up_due', gjelder: 'en oppfølging skal sendes', gruppe: 'betaprogram' },
  { navn: 'program.closed', gjelder: 'et program ble avsluttet eller avbrutt', gruppe: 'betaprogram' },
  { navn: 'hypothesis.submitted', gjelder: 'en hypotese ble sendt inn', gruppe: 'hypoteser' },
  { navn: 'hypothesis.revised', gjelder: 'en hypotese ble endret', gruppe: 'hypoteser' },
  { navn: 'hypothesis.status_changed', gjelder: 'en hypotese fikk ny status', gruppe: 'hypoteser' },
  { navn: 'hypothesis.needs_more', gjelder: 'innsenderen ble bedt om mer', gruppe: 'hypoteser' },
  { navn: 'hypothesis.deadline_warning', gjelder: 'en frist for en hypotese er i morgen', gruppe: 'hypoteser' },
  { navn: 'hypothesis.deadline_passed', gjelder: 'en frist for en hypotese er passert', gruppe: 'hypoteser' },
  { navn: 'hypothesis.revisit_due', gjelder: 'en parkert eller pauset hypotese skal tas opp igjen', gruppe: 'hypoteser' },
  { navn: 'hypothesis.meeting_to_confirm', gjelder: 'et møte venter på bekreftelse', gruppe: 'hypoteser' },
  { navn: 'hypothesis.concluded', gjelder: 'en hypotese ble konkludert eller stoppet', gruppe: 'hypoteser' },
  { navn: 'submission.conclusion_notice', gjelder: 'brukerne som meldte fra fikk «Hva vi fant ut»', gruppe: 'hypoteser' },
];

/**
 * «Sender ved» (skjerm 14): én eller to hendelser i setningsform, ellers per gruppe — «Hypoteser · 6
 * av 10». Regelen før H3.5 («betaprogrammet · N hendelser» for alt over tre) ga feil svar for en
 * mottaker med fire saks- eller hypotesehendelser (konseptsidens avvik 15).
 */
export function senderVed(events: readonly WebhookEventType[]): string {
  if (events.length <= 2) return events.map((e) => HENDELSER.find((h) => h.navn === e)?.gjelder ?? e).join(' · ');
  return GRUPPER.map((g) => {
    const alle = HENDELSER.filter((h) => h.gruppe === g.id);
    const valgt = alle.filter((h) => events.includes(h.navn)).length;
    return valgt > 0 ? `${g.navn} · ${valgt} av ${alle.length}` : null;
  })
    .filter(Boolean)
    .join(' · ');
}

/** Nøklene eies her og bare her (`react-ts-integration`). */
export const webhookNoekkel = {
  alle: ['webhooks'] as const,
  liste: ['webhooks', 'liste'] as const,
  leveringer: (id: string) => ['webhooks', 'leveringer', id] as const,
};

function kast(res: { response?: Response; data?: unknown; error?: unknown }): never {
  throw ApiFeil.fra(res.response?.status ?? 0, res.error ?? res.data);
}

/** Registrerte webhooks. Secreten finnes aldri her (DD-20). */
export function useWebhooks() {
  const klient = useApiKlient();
  return useQuery({
    queryKey: webhookNoekkel.liste,
    queryFn: async (): Promise<Webhook[]> => {
      const res = await webhooksList({ client: klient });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as Webhook[];
    },
  });
}

/** Leveringsloggen for én webhook — dead er dead-letter-lista som skal være synlig (låst). */
export function useLeveringer(id: string | null) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: webhookNoekkel.leveringer(id ?? ''),
    enabled: id !== null,
    queryFn: async (): Promise<WebhookDelivery[]> => {
      const res = await webhooksListDeliveries({ client: klient, path: { id: id! }, query: { limit: 50 } });
      if (!res.response?.ok || !res.data) kast(res);
      return (res.data as { items: WebhookDelivery[] }).items;
    },
  });
}

function useKall<TArg, TRes>(fn: (arg: TArg) => Promise<{ response?: Response; data?: unknown; error?: unknown }>, etter: (res: TRes, arg: TArg) => void) {
  return useMutation({
    mutationFn: async (arg: TArg): Promise<TRes> => {
      const res = await fn(arg);
      if (!res.response?.ok) kast(res);
      return res.data as TRes;
    },
    onSuccess: etter,
  });
}

/** Opprett: svaret bærer secreten — den ene gangen (DD-20). Kun eier. */
export function useOpprettWebhook() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useKall<WebhookCreate, Webhook>(
    (body) => webhooksCreate({ client: klient, body }),
    () => void queryClient.invalidateQueries({ queryKey: webhookNoekkel.alle }),
  );
}

export function useOppdaterWebhook() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useKall<{ id: string; body: WebhookUpdate }, Webhook>(
    ({ id, body }) => webhooksUpdate({ client: klient, path: { id }, body }),
    () => void queryClient.invalidateQueries({ queryKey: webhookNoekkel.alle }),
  );
}

export function useSlettWebhook() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useKall<string, void>(
    (id) => webhooksRemove({ client: klient, path: { id } }),
    () => void queryClient.invalidateQueries({ queryKey: webhookNoekkel.alle }),
  );
}

/** Verifiser: mottakeren må gjenta challenget. ok = true setter verifiedAt. Kun eier. */
export function useVerifiserWebhook() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useKall<string, WebhookProbeResult>(
    (id) => webhooksVerify({ client: klient, path: { id } }),
    () => void queryClient.invalidateQueries({ queryKey: webhookNoekkel.alle }),
  );
}

/** Testknappen: én signert webhook.test, synkront, ingen retry. Behandler og eier. */
export function useTestWebhook() {
  const klient = useApiKlient();
  return useKall<string, WebhookProbeResult>((id) => webhooksTest({ client: klient, path: { id } }), () => undefined);
}

/** Roter: den nye secreten finnes kun i dette svaret; den gamle er ugyldig straks. Kun eier. */
export function useRoterSecret() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useKall<string, Webhook>(
    (id) => webhooksRotateSecret({ client: klient, path: { id } }),
    () => void queryClient.invalidateQueries({ queryKey: webhookNoekkel.alle }),
  );
}

/** Dead-letter på nytt — én gang, samme event_id. Behandler og eier. */
export function useSendPaaNytt() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useKall<{ id: string; deliveryId: string }, WebhookDelivery>(
    ({ id, deliveryId }) => webhooksRetryDelivery({ client: klient, path: { id, deliveryId } }),
    (_res, { id }) => void queryClient.invalidateQueries({ queryKey: webhookNoekkel.leveringer(id) }),
  );
}
